// Step 3: InfluencerBio for signup wizard
// Final step: bio creation + profile picture
// Fields moved from old Step 1: profile picture upload

/*
QA Test Cases:
- Step 3 rejects submission if bio < 50 chars
- Upload shows "Uploading…" then a thumbnail; state includes media.profilePictureUrl
- Finish writes onboardingComplete and profile object with media merged
- Refresh on Step 3 restores profilePictureUrl from localStorage draft
- Profile picture is optional for MVP (no validation blocking)
*/

import React, { useState, useEffect, useRef } from 'react';

const InfluencerBio = ({ data, onDataChange, user }) => {
  // Editable copy constants
  const COPY = {
    title: 'Bio & Content',
    subtitle: 'Let\'s make your bio!',
    nextButton: 'Next',
    bioHelper: 'Write 2-4 sentences about yourself, your content, and what makes you unique. This will be visible to brands on your profile.'
  };

  const [formData, setFormData] = useState({
    bio: {
      text: data?.bio?.text || ''
    },
    media: {
      profilePictureFile: data?.media?.profilePictureFile || null
    }
  });
  
  const [profileImagePreview, setProfileImagePreview] = useState(null);

  // Update parent component when form data changes
  // Use a ref to track if we've already sent this data to prevent infinite loops
  const lastSentDataRef = useRef(null);
  
  useEffect(() => {
    const dataString = JSON.stringify(formData);
    if (lastSentDataRef.current !== dataString) {
      lastSentDataRef.current = dataString;
      onDataChange(formData);
    }
  }, [formData, onDataChange]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const [section, field] = name.split('.');
    
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0] || null;
    
    if (!file) {
      setFormData(prev => ({
        ...prev,
        media: {
          ...prev.media,
          profilePictureFile: null
        }
      }));
      setProfileImagePreview(null);
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    // Store file in form data
    setFormData(prev => ({
      ...prev,
      media: {
        ...prev.media,
        profilePictureFile: file
      }
    }));

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfileImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };


  // Validation function - Step 3 validation rules
  const validate = async (stepData) => {
    const errors = [];
    const bio = stepData.bio || {};
    
    // Bio validation: required and minimum 50 characters
    if (!bio.text?.trim()) {
      errors.push('Bio is required');
    } else if (bio.text.trim().length < 50) {
      errors.push('Bio must be at least 50 characters long');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  };

  // Attach validate function to component
  InfluencerBio.validate = validate;

  return (
    <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{COPY.subtitle}</h3>
        
        {/* Profile Photo Section (Optional) */}
        <div className="mb-8">
          <h4 className="text-md font-medium text-gray-900 mb-4">Profile Photo</h4>
          <div className="space-y-2">
            <label htmlFor="profile-photo" className="block text-sm font-medium text-gray-700">
              Profile Photo <span className="text-gray-400 text-xs">(optional)</span>
            </label>
            {profileImagePreview && (
              <div className="mb-2">
                <img
                  src={profileImagePreview}
                  alt="Profile preview"
                  className="h-16 w-16 rounded-full object-cover border border-gray-300"
                />
              </div>
            )}
            <input
              id="profile-photo"
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="block w-full text-sm text-gray-600
                file:mr-4 file:py-2 file:px-4
                file:rounded-md file:border-0
                file:text-sm file:font-semibold
                file:bg-blue-50 file:text-blue-700
                hover:file:bg-blue-100"
            />
            <p className="text-xs text-gray-400">
              Recommended: clear headshot. Max 5MB. JPG, PNG, or GIF.
            </p>
          </div>
        </div>

        {/* Bio Section */}
        <div className="mb-8">
          <h4 className="text-md font-medium text-gray-900 mb-4">Bio</h4>
          <div className="mb-6">
            <label htmlFor="bio.text" className="form-label">
              Bio *
            </label>
            <textarea
              id="bio.text"
              name="bio.text"
              rows={4}
              value={formData.bio.text}
              onChange={handleChange}
              className="form-input"
              placeholder="Write 2-4 sentences about yourself, your content, and what makes you unique..."
            />
            <p className="mt-1 text-xs text-gray-500">
              {COPY.bioHelper}
            </p>
            <p className="mt-1 text-xs text-gray-400">
              {formData.bio.text.length}/50 characters minimum
            </p>
          </div>
        </div>
    </div>
  );
};

export default InfluencerBio;